import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { RED, MUTED } from "assets/theme/brandColors";

// Neutrals borrowed from the surrounding module view (card border, button
// greys) rather than invented here — see Ingredients.js and the builder's
// review overlay. Text/background pairs in this file: #fff on RED (8.5:1),
// #444 on #f0f0f0 (8.6:1), MUTED on #fff (5.3:1), #222 on #fff (16:1).
const CARD_BORDER = "#c8ccd4";
const RED_HOVER = "#7d1520";
const GREY_BTN = "#f0f0f0";
const GREY_BTN_HOVER = "#e4e4e4";

const btn = (filled) => ({
  border: "none",
  borderRadius: "8px",
  px: 1.75,
  py: "8px",
  fontSize: 13,
  fontWeight: 700,
  fontFamily: "Roboto, sans-serif",
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "background 0.15s",
  ...(filled
    ? { bgcolor: RED, color: "#fff", "&:hover": { bgcolor: RED_HOVER } }
    : { bgcolor: GREY_BTN, color: "#444", "&:hover": { bgcolor: GREY_BTN_HOVER } }),
});

/**
 * Repair controls for one recipe of a committed schedule.
 *
 * Only rendered while the week is unlocked — the caller drops it entirely once
 * the first recipe has been completed, so this component never has to reason
 * about a locked state. The copy line is the other half of that rule: the
 * controls vanish the moment cooking starts, and a disappearance nobody was
 * warned about reads as a bug.
 *
 * Rendered as the recipe header's `headerSlot`, i.e. under the recipe title
 * rather than above it. It used to open the screen, so "Your week locks once
 * you start cooking" was the first sentence a cook read — a warning about an
 * edit control, before they had seen which dish they were looking at.
 *
 * Remove is a two-step confirm because there is no way back — a schedule can
 * shrink but nothing adds a recipe to an existing week.
 */
function ScheduleEditControls({ recipe, canRemove, busy, onReplace, onRemove }) {
  const [confirming, setConfirming] = useState(false);

  // Moving to another recipe cancels a pending confirm rather than carrying it
  // across, so the second tap can't land on a dish the user never armed.
  useEffect(() => setConfirming(false), [recipe.id]);

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: CARD_BORDER,
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        opacity: busy ? 0.55 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#222" }}>
          {confirming ? `Remove “${recipe.recipe_title}” from this week?` : "Not feeling this one?"}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: MUTED, mt: "2px" }}>
          {confirming
            ? "Your week gets shorter — nothing takes its place."
            : "Your week locks once you start cooking."}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {confirming ? (
          <>
            <Box component="button" disabled={busy} onClick={onRemove} sx={btn(true)}>
              {busy ? "Removing…" : "Remove"}
            </Box>
            <Box component="button" onClick={() => setConfirming(false)} sx={btn(false)}>
              Keep it
            </Box>
          </>
        ) : (
          <>
            <Box component="button" disabled={busy} onClick={onReplace} sx={btn(true)}>
              ↺ Replace
            </Box>
            {canRemove && (
              <Box
                component="button"
                disabled={busy}
                onClick={() => setConfirming(true)}
                sx={btn(false)}
              >
                Remove
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

ScheduleEditControls.propTypes = {
  recipe: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    recipe_title: PropTypes.string,
  }).isRequired,
  // False on the last remaining recipe: a schedule of nothing is not a plan,
  // and the backend refuses it too.
  canRemove: PropTypes.bool,
  busy: PropTypes.bool,
  onReplace: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ScheduleEditControls;
